import { useEffect, useState } from "react";
import { HERO_BG, heroSrcSet, siteMobileSrcSet, localHeroDesktop, MOBILE_MEDIA, DESKTOP_MEDIA } from "@/lib/images";
import { positionCss } from "@/components/admin/ImagePositionPicker";
import { useLang } from "@/context/LanguageContext";
import { dv } from "@/lib/theme";
import { dropShell } from "@/lib/shell";

const PRELOAD_KEY = "ph_hero";
const readCache = () => { try { return JSON.parse(localStorage.getItem(PRELOAD_KEY) || "null") || {}; } catch { return {}; } };
const writeCache = (patch) => { try { localStorage.setItem(PRELOAD_KEY, JSON.stringify({ ...readCache(), ...patch })); } catch { /* localStorage gesperrt */ } };

/** Titelbild der Startseite: Handys bekommen den Hochformat-Ausschnitt (mobil/…-480/640/828.webp), größere Bildschirme WebP in 720/1080/1440/1920 px, JPG-Fallback;
 *  hohe Ladepriorität; merkt sich URLs und Geometrie für Preload und Hülle in index.html. Solange die Einstellungen noch laden, zeigt es sofort das in index.html
 *  vorgeladene Bild (window.__phHero). Das Musterbild erscheint nur, wenn kein eigenes Titelbild existiert. */
export const HeroImage = ({ settings, alt }) => {
  const { siteLoaded } = useLang();
  const [broken, setBroken] = useState(false);
  const loaded = Object.keys(settings).length > 0;
  const hint = !loaded && typeof window !== "undefined" ? window.__phHero : null;
  const src = settings.home_hero_image || (siteLoaded ? HERO_BG : "") || (hint?.href || "");
  // lokale Kopie bevorzugen – funktioniert auch, wenn die WebP-Erzeugung auf dem Bildserver (noch) nicht gelaufen ist
  const webp = settings.home_hero_image && !broken ? (localHeroDesktop(settings.home_hero_image) || settings.home_hero_image_webp) : "";
  const webpSm = settings.home_hero_image ? settings.home_hero_image_webp_sm : "";
  const srcSet = webp ? heroSrcSet(webp) || (webpSm ? `${webpSm} 960w, ${webp} 1920w` : "") : (hint?.srcset || "");
  const mobile = settings.home_hero_image && !broken ? siteMobileSrcSet(settings.home_hero_image) : (hint?.mobile || "");
  const position = settings.home_hero_image ? positionCss(settings.home_hero_position) : (hint?.position || "center");
  useEffect(() => {
    if (loaded) writeCache({ href: webp || src, srcset: srcSet, type: webp ? "image/webp" : "", mobile, position });
  }, [src, webp, srcSet, mobile, position, loaded]);
  useEffect(() => dropShell, []);
  const onLoad = (e) => {
    const img = e.currentTarget;
    (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(dropShell);
    // Geometrie für die Hülle beim nächsten Besuch merken – erst im Leerlauf messen, sonst erzwingt getBoundingClientRect hier ein teures Layout
    const measure = () => { if (!img.isConnected) return; const r = img.getBoundingClientRect(); writeCache({ geo: { vw: window.innerWidth, hh: Number(dv(settings, "hero_height")) || 92, top: Math.round(r.top + window.scrollY), h: Math.round(r.height) } }); };
    if (window.requestIdleCallback) window.requestIdleCallback(measure, { timeout: 3000 }); else setTimeout(measure, 1500);
  };
  if (!src) return <div className="absolute inset-0 bg-stone-800" data-testid="home-hero-img-pending" />;
  const webpSrcSet = webp ? (srcSet || webp) : (hint ? srcSet : "");
  return (
    <picture>
      {mobile && <source media={MOBILE_MEDIA} type="image/webp" srcSet={mobile} sizes="100vw" data-testid="home-hero-mobile-source" />}
      {webpSrcSet && <source media={mobile ? DESKTOP_MEDIA : undefined} type="image/webp" srcSet={webpSrcSet} sizes="100vw" />}
      <img src={src} alt={alt} width="1920" height="1080" className="hero-img" style={{ objectPosition: position }} fetchPriority="high" decoding="async"
           onLoad={onLoad} onError={() => { setBroken(true); dropShell(); }} data-testid="home-hero-img" />
    </picture>
  );
};
